import { css } from '@emotion/react';
import {
  type FC, useMemo, useState,
} from 'react';
import { type GroupingColumn } from '~/_shared/types/grouping/grouping';
import { useSpreadsheetColumnRowValues } from '~/_shared/utils/spreadsheet/useSpreadsheetColumnRowValues.hook';
import { useGetColumnATOMetrics } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/useGetColumnATOMetrics';
import { usePerGroupVisualSettings } from '~/store/mapSettings/makersGeneral/mapSettingsMarkersGeneralSelectors.hooks';
import { getMatchupDataColumnName } from '~/store/matchupData/matchupData.helpers';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';
import { ModalComponent } from '../_shared/components/modal/modal.component';
import { type SpreadsheetRowId } from '../_shared/types/spreadsheetData/spreadsheetRow';
import { useTranslation } from '../_shared/utils/hooks';
import { createPieChartItems } from '../_shared/utils/maptive/pieCharts/pieCharts.helpers';
import { type ModalProps } from '../modal/modalType.enum';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '../store/mapSettings/grouping/mapSettingsGrouping.selectors';
import { useMatchupDataSelector } from '../store/matchupData/matchupData.selectors';
import { useSpreadsheetDataDataSelector } from '../store/spreadsheetData/spreadsheetData.selectors';
import { ChartsVisualizerComponent } from './chartsVisualizer.component';
import {
  type MetricOption, MetricOptionKind,
} from './chartsVisualizer.types';

const modalContentStyle = css({
  padding: '0px',
  width: 547,
});

export type ChartsModalProps = ModalProps & Readonly<{
  modalCaption: string;
  spreadsheetRowIds: SpreadsheetRowId[];
}>;

export const ChartsModalContainer: FC<ChartsModalProps> = ({ spreadsheetRowIds, modalCaption, isOpen, onClose }) => {
  const activeGroupColumns = useMapSettingsGroupingActiveGroupColumnsSelector();
  const primaryGroupColumn: GroupingColumn | undefined = activeGroupColumns[0];
  const secondaryGroupColumn: GroupingColumn | undefined = activeGroupColumns[1];
  const spreadsheetData = useSpreadsheetDataDataSelector();
  const matchupData = useMatchupDataSelector();
  const perGroupVisualSettings = usePerGroupVisualSettings();
  const [t] = useTranslation();
  const spreadsheetId = usePrimarySpreadsheetId();
  const { columnMetrics, isLoading: isMetricLoading } = useGetColumnATOMetrics(spreadsheetId, { includeGroups: false });

  const numericColumnIds = useMemo(() =>
    new Set(columnMetrics.filter(m => m.isNumeric).map(m => m.columnId)),
  [columnMetrics]);

  const allOptions = useMemo((): MetricOption[] => [
    { kind: MetricOptionKind.LocationCount, name: t('locationCount') },
    ...Object.entries(matchupData).flatMap(([spreadsheetId, entry]) =>
      Object.entries(entry.data?.columns ?? {})
        .filter(([columnId]) => numericColumnIds.has(columnId))
        .map(([columnId, name]): MetricOption => ({
          kind: MetricOptionKind.Column,
          spreadsheetId: Number(spreadsheetId),
          columnId,
          name,
        }))
    ),
  ], [matchupData, numericColumnIds, t]);

  const [selectedMetric, setSelectedMetric] = useState<MetricOption | null>(allOptions[0] ?? null);

  const selectedMetricColumn = useMemo(() => (
    selectedMetric?.kind === MetricOptionKind.Column
      ? { spreadsheetId: selectedMetric.spreadsheetId, columnId: selectedMetric.columnId }
      : null
  ), [selectedMetric]);

  const { rowValues: selectedMetricColumnValues, isLoading: isMetricColumnValuesLoading } = useSpreadsheetColumnRowValues(selectedMetricColumn, DataType.NUMBER);

  const getLocationValue = useMemo(() => {
    if (!selectedMetricColumn) {
      return undefined;
    }

    return (locationId: SpreadsheetRowId): number | null => {
      if (locationId.spreadsheetId !== selectedMetricColumn.spreadsheetId) {
        return null;
      }

      return selectedMetricColumnValues.get(locationId.rowId) ?? null;
    };
  }, [selectedMetricColumn, selectedMetricColumnValues]);

  const isChartDataLoading = !!selectedMetricColumn && isMetricColumnValuesLoading;

  const chartData = useMemo(() => {
    if (!spreadsheetData || isChartDataLoading) {
      return null;
    }

    const params = { markerIds: spreadsheetRowIds, activeGroupColumns, spreadsheetData, perGroupVisualSettings, matchupData, getLocationValue };
    return {
      chartItems: createPieChartItems({ ...params, hierarchy: 0 }),
      secondaryChartItems: createPieChartItems({ ...params, hierarchy: 1 }),
    };
  }, [spreadsheetData, isChartDataLoading, spreadsheetRowIds, activeGroupColumns, perGroupVisualSettings, matchupData, getLocationValue]);

  const primaryColumnName = useMemo(() => (
    primaryGroupColumn ? getMatchupDataColumnName(matchupData, primaryGroupColumn) ?? primaryGroupColumn.columnId : ''
  ), [primaryGroupColumn, matchupData]);

  const secondaryColumnName = useMemo(() => (
    secondaryGroupColumn ? getMatchupDataColumnName(matchupData, secondaryGroupColumn) ?? secondaryGroupColumn.columnId : undefined
  ), [secondaryGroupColumn, matchupData]);

  return (
    <ModalComponent
      caption={modalCaption}
      contentMaxHeight="90%"
      contentStyle={modalContentStyle}
      isOpen={isOpen}
      maxWidth={547}
      onClose={onClose}
    >
      <ChartsVisualizerComponent
        allOptions={allOptions}
        data={chartData}
        isChartDataLoading={isChartDataLoading}
        isMetricLoading={isMetricLoading}
        onSelectedMetricChange={setSelectedMetric}
        primarySourceLabel={primaryColumnName}
        secondarySourceLabel={secondaryColumnName}
        selectedMetric={selectedMetric}
      />
    </ModalComponent>
  );
};
